/**
 * Reading resolved values off a {@link CandidateRow}'s cells.
 *
 * The lowest layer of the import pipeline: no product, recipe or element
 * knowledge, so both the quantity resolver and materialization can sit on top
 * of it without importing each other.
 */
import type { CandidateRow } from '@shared/models/element-import.model';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import type { NodonField } from './kalkylfil-csv.parser';
import { removeNumberFormatting } from './math.helpers';
import { unitAndQuantityHarmonizer } from './unit.helpers';

/**
 * Trimmed value of the cell that won `field` for this row — `undefined` when
 * no column resolved to that field or the cell is empty. The parser tags
 * exactly one cell per row with each `nodonField`.
 */
export const candidateFieldValue = (
  row: CandidateRow,
  field: NodonField,
): string | undefined => {
  const cell = row.cells.find((c) => c.nodonField === field);
  const trimmed = String(cell?.value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Parse a CSV cell that should hold a number, tolerating a trailing '%' and
 * locale formatting (',' decimals, thousand separators, e.g. `1 234,56`).
 */
export const parseNumericCell = (value: string): number | undefined => {
  const trimmed = value.replace('%', '').trim();
  if (trimmed.length === 0) return undefined;
  const n = Number(removeNumberFormatting(trimmed));
  return Number.isFinite(n) ? n : undefined;
};

/** The raw CSV unit string for a candidate row, when a column carries it. */
export const candidateUnitFrom = (row: CandidateRow): string | undefined =>
  candidateFieldValue(row, 'unit');

/** The numeric quantity for a candidate row, when a column carries it. */
export const candidateQuantityFrom = (
  row: CandidateRow,
): number | undefined => {
  const v = candidateFieldValue(row, 'quantity');
  return v === undefined ? undefined : parseNumericCell(v);
};

/**
 * Harmonize a raw CSV unit string. `unitAndQuantityHarmonizer` throws on
 * unrecognised units (e.g. CSV "x", "frp"); for a candidate row that just means
 * we cannot auto-convert — the caller falls back to a manual factor.
 */
export const harmonizeCandidateUnitString = (
  unit: string,
): { unit: ConversionFactorUnit; quantity: number } | undefined => {
  try {
    return unitAndQuantityHarmonizer(1, unit) ?? undefined;
  } catch {
    return undefined;
  }
};
